import {
    BaseRouteStep,
    ParsedTransactionInstruction,
    RouteStepInstructions,
    RouteStepQuote,
    SwapInstructionsResponse
} from "@loopscale/pandora-types";
import { SwapCollateralTxnParams, TokenListMetadata } from "@bridgesplit/abf-sdk";
import { getAssetData } from "@bridgesplit/abf-react";

// Route step instructions combined with optional route metadata when available
export type RouteInstructionStep = RouteStepInstructions & Partial<BaseRouteStep & RouteStepQuote>;

// One self-contained slice of a step (pandora `splitRolloverLegs`). Declared here until the
// published pandora-types carries it.
export interface RouteStepLeg {
    inputAmount: number;
    minOutputAmount: number;
    ixs: ParsedTransactionInstruction[];
}

export function getRouteStepLegs(step?: RouteInstructionStep): RouteStepLeg[] {
    return (step as { legs?: RouteStepLeg[] } | undefined)?.legs ?? [];
}

/**
 * Normalizes swap instructions into a list of steps, preferring routeSteps (with mint metadata)
 * and falling back to swapInstructions.
 */
export function getSwapInstructionSteps(quote?: SwapInstructionsResponse): RouteInstructionStep[] {
    if (!quote) return [];

    const routeSteps: RouteInstructionStep[] =
        quote.routeSteps?.map((step) => ({
            // Route steps from the quote do not include instructions, so default them to empty arrays
            setupIxs: [],
            ixs: [],
            luts: [],
            ...step
        })) ?? [];
    const swapInstructions = quote.swapInstructions;

    if (swapInstructions?.length) {
        if (routeSteps.length === swapInstructions.length && routeSteps.length > 0) {
            // Combine instructions with their corresponding route metadata when lengths align
            return swapInstructions.map((step, idx) => ({ ...routeSteps[idx], ...step }));
        }
        return swapInstructions;
    }

    if (routeSteps.length) return routeSteps;

    return [];
}

export function getPrimarySwapInstructionStep(quote?: SwapInstructionsResponse): RouteInstructionStep | undefined {
    return getSwapInstructionSteps(quote)[0];
}

export function mergeRouteInstructionSteps(steps: RouteInstructionStep[]): RouteInstructionStep | undefined {
    if (!steps.length) return undefined;

    const [first, ...rest] = steps;
    const merged: RouteInstructionStep = {
        ...first,
        outputMint: rest.length ? (rest[rest.length - 1].outputMint ?? first.outputMint) : first.outputMint,
        minOutputAmount: rest.length
            ? (rest[rest.length - 1].minOutputAmount ?? first.minOutputAmount)
            : first.minOutputAmount,
        setupIxs: [...(first.setupIxs ?? [])],
        ixs: [...(first.ixs ?? [])],
        luts: [...(first.luts ?? [])],
        signers: [...(first.signers ?? [])]
    };

    for (const step of rest) {
        merged.setupIxs.push(...(step.setupIxs ?? []));
        merged.ixs.push(...(step.ixs ?? []));
        merged.luts.push(...(step.luts ?? []));
        merged.signers?.push(...(step.signers ?? []));
    }

    merged.luts = Array.from(new Set(merged.luts));
    merged.signers = merged.signers ? Array.from(new Set(merged.signers)) : merged.signers;
    // Multi-hop swap routes often produce the same CreateIdempotent ATA ix on more than one leg
    // (e.g. both legs need the intermediate mint's ATA). The loan builder rejects tx bundles that
    // contain duplicate instructions, so dedupe before returning.
    merged.setupIxs = dedupeInstructions(merged.setupIxs);
    merged.ixs = dedupeInstructions(merged.ixs);

    return merged;
}

function dedupeInstructions<T>(ixs: T[]): T[] {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const ix of ixs) {
        const key = JSON.stringify(ix);
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(ix);
    }
    return out;
}

/**
 * Split a swap step into the shape expected by downstream tx builders.
 *
 * By default `cpiIxs` includes any hoisted setupIxs (flattened) so the caller can inline
 * everything into a single tx. Pass `flattenSetup: false` when you plan to forward `setupIxs`
 * to a builder slot that will emit them as a separate tx (e.g. flash_borrow's `setup_ixs` with
 * `unifySetup: false`); otherwise the ATA CreateIdempotent ixs end up duplicated in both txs.
 */
export function getCpiFromSwapStep(
    step?: RouteInstructionStep,
    { flattenSetup = true }: { flattenSetup?: boolean } = {}
) {
    const setupIxs = step?.setupIxs ?? [];
    const ixs = step?.ixs ?? [];
    return {
        cpiIxs: flattenSetup ? [...setupIxs, ...ixs] : ixs,
        cpiLuts: step?.luts ?? [],
        cpiSigners: step?.signers ?? [],
        setupIxs,
        setupSigners: step?.signers ?? []
    };
}

export function buildSwapCollateralParamsFromStep({
    loanAddress,
    step,
    resolveMetadataForMint,
    collateralAmount,
    depositAmount,
    omitSetup = false
}: {
    loanAddress: string;
    collateralAmount?: number;
    depositAmount?: number;
    step: RouteInstructionStep;
    resolveMetadataForMint: (mint: string | undefined) => TokenListMetadata | undefined;
    /**
     * True when the caller is responsible for this step's setup ixs elsewhere (e.g. a composite
     * wind that hoists all setup ixs into flash_borrow's setup slot for a single combined setup tx).
     * In that case we emit `cpiIxs` as main-only and omit the setup fields entirely so BE builds
     * a single main tx for this step.
     */
    omitSetup?: boolean;
}): SwapCollateralTxnParams | undefined {
    if (!step.inputMint || !step.outputMint || step.inputAmount === undefined) return undefined;

    const outputMetadata = resolveMetadataForMint(step.outputMint);
    if (!outputMetadata) return undefined;

    const collateralAssetData = getAssetData(outputMetadata, step.outputMint);
    if (!collateralAssetData) return undefined;

    const { cpiIxs, cpiLuts, cpiSigners, setupIxs, setupSigners } = getCpiFromSwapStep(step, {
        flattenSetup: false
    });

    const depositCollateralAmount =
        depositAmount !== undefined
            ? depositAmount
            : step.minOutputAmount !== undefined
              ? Math.max(step.minOutputAmount - 1, 0)
              : undefined;
    if (depositCollateralAmount === undefined) return undefined;

    const base: SwapCollateralTxnParams = {
        loan: loanAddress,
        withdrawCollateral: [
            {
                collateralMint: step.inputMint,
                // need to withdraw full collateral amount
                amount: collateralAmount ?? step.inputAmount
            }
        ],
        depositCollateral: [
            {
                collateralAssetData,
                collateralAmount: depositCollateralAmount
            }
        ],
        cpiIxs,
        cpiLuts,
        cpiSigners
    };

    if (omitSetup) return base;

    // Mirror pandora's setupIxs into the BE swap_collateral setup slot so ATA creates land in
    // their own tx (Jito bundle) rather than inflating the main. Empty setupIxs → BE no-ops and
    // emits a single main tx as before.
    return {
        ...base,
        setupIxs,
        setupLuts: cpiLuts,
        setupSigners,
        unifySetup: false
    };
}
